import { useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useContactActions, useContactState } from '../useApi/useContact';
import { useContactStore } from '../../store/contactStore';
import { useEditMode } from '../useEditMode';
import {
  useContactPropertyVisibleActions,
  useContactPropertyVisibleState,
} from '../useApi/useContactPropertyVisible';
import { ContactPropertyVisibleData, SocialMedia } from '../../dto/EntityRepository';
import { AlertPosition, AlertType, DeviceType } from '../../enum/EnumRepository';
import { useStandardAlertStore } from '../../store/portal/alertStore';
import { getAlertMessage } from '../../constants/alertMessages';
import { useDeviceStore } from '../../store/deviceStore';
import { useIsPageOwner } from '../useIsPageOwner';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { isPageOwnerMatchedToRaw } from '../../util/pageOwnerMatch';
import { UpdateContactReq } from '../../dto/ReqDtoRepository';
import { useValidation } from '../useValidation';
import { isValidHttpUrl } from '../../util/urlValidation';
import {
  canShowContact,
  canShowContactPropertyVisible,
} from '../../util/visibility/contactVisibility';
import type { ResourceScope } from '../../util/scopedResource';

export function useContactPage() {
  const isOwner = useIsPageOwner();
  const { isEditMode } = useEditMode();
  const { contact, contactOwnerAui, contactScope, status } = useContactState();
  const { getContact, getContactOwner, updateContact } = useContactActions();
  const hasChanged = useContactStore((s) => s.hasChanged);
  const handleChange = useContactStore((s) => s.updateContact);
  const updateSns = useContactStore((s) => s.updateSns);
  const {
    contactPropertyVisible,
    visibleOwnerAui,
    status: propertyVisibleStatus,
  } = useContactPropertyVisibleState();
  const { updateContactPropertyVisible, getContactPropertyVisible } =
    useContactPropertyVisibleActions();
  const { deviceType } = useDeviceStore();
  const { enqueueAlert } = useStandardAlertStore();
  const { isEmail } = useValidation();
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const { HANDLE: raw } = useParams<{ HANDLE?: string }>();
  const currentScope: ResourceScope = isOwner ? 'owner' : 'public';
  const canShow =
    canShowContact({
      pageOwnerAui: pageOwner?.aui,
      contactOwnerAui,
      contactScope,
      currentScope,
      status,
    }) &&
    canShowContactPropertyVisible({
      pageOwnerAui: pageOwner?.aui,
      visibleOwnerAui,
      status: propertyVisibleStatus,
    });

  useLayoutEffect(() => {
    const fetchContactData = async () => {
      if (!isPageOwnerMatchedToRaw(raw, pageOwner)) return;

      // isOwner로 확인하여 적절한 API 호출
      // isOwner는 authToken이 설정된 후에만 true가 됨
      await Promise.all([
        isOwner ? getContactOwner() : getContact(),
        getContactPropertyVisible()
      ]);
    }
    fetchContactData();
  }, [raw, pageOwner?.aui, isOwner]);

  const handleSnsChange = (updates: Partial<SocialMedia>) => {
    const cleanedUpdates: Partial<SocialMedia> = {};

    for (const [key, value] of Object.entries(updates)) {
      if (typeof value === "string") {
        const v = value.trim();
        cleanedUpdates[key as keyof SocialMedia] = v.startsWith("@") ? v.slice(1) : v;
      } else {
        cleanedUpdates[key as keyof SocialMedia] = value as any;
      }
    }

    updateSns(cleanedUpdates);
  };

  const handleDoubleClick = async (field: keyof ContactPropertyVisibleData) => {
    if (!isEditMode || !contactPropertyVisible) return null;
    await updateContactPropertyVisible({
      contactPropertyVisibleId: contactPropertyVisible.id,
      ...contactPropertyVisible,
      [field]: !contactPropertyVisible[field]
    });
  };

  const changeVisibleMobile = (field: keyof ContactPropertyVisibleData) => {
    if (deviceType === DeviceType.MOBILE) {
      enqueueAlert({
        type: AlertType.CONFIRM,
        position: AlertPosition.TOP,
        content: getAlertMessage('CONFIRM_CHANGE_VISIBILITY'),
        callBack: () => {
          handleDoubleClick(field);
        }
      });
    }
  }

  const handleConfirm = async () => {
    if (!hasChanged || !contact) return;

    if (contact.email && !isEmail(contact.email)) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_EMAIL_FORMAT'),
      });
      return;
    }

    if (contact.sns?.url1 && !isValidHttpUrl(contact.sns.url1)) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_URL_FORMAT'),
      });
      return;
    }

    const updateContactReq: UpdateContactReq = {
      ...contact,
      ...contact.sns
    };

    await updateContact(updateContactReq);
  };

  return {
    canShow,
    contact,
    contactPropertyVisible,
    isEditMode,
    deviceType,
    hasChanged,
    handleChange,
    handleSnsChange,
    handleDoubleClick,
    changeVisibleMobile,
    handleConfirm,
    igValue: contact?.sns?.instagram ?? "",
    twValue: contact?.sns?.twitter ?? "",
    fbValue: contact?.sns?.facebook ?? "",
    url1Value: contact?.sns?.url1 ?? "",
  };
}
